/**
 * A debounce that keeps its pending value committed-or-flushable.
 *
 * The editor uses it for draft commits: typing schedules, the timer fires on
 * quiet — but the value paired with the callback captured at schedule time can
 * also be flushed synchronously (note switch, unmount). A plain timer would
 * either keep running against a new selection or be cancelled outright, which
 * is how quick switches used to eat the last edits.
 */
export class DebouncedCommit<T> {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pendingValue: T | undefined;
  private pendingCommit: ((value: T) => void) | undefined;

  constructor(private readonly delayMs: number) {}

  /** True while a value is scheduled but not yet committed. */
  get pending(): boolean {
    return this.timer !== null;
  }

  /**
   * Schedules (or reschedules) the debounce.
   *
   * `commit` is captured per schedule: a later `flush` must run the callback
   * that was current when the value was produced, not whichever one a re-render
   * happens to have handy — the callback closes over the note being edited.
   */
  schedule(value: T, commit: (value: T) => void): void {
    this.pendingValue = value;
    this.pendingCommit = commit;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fire(), this.delayMs);
  }

  /** Fires the in-flight value now. No-op when nothing is scheduled. */
  flush(): void {
    if (this.timer === null) return;
    this.fire();
  }

  /** Drops the pending value without committing it. */
  cancel(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
    this.pendingValue = undefined;
    this.pendingCommit = undefined;
  }

  private fire(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    const value = this.pendingValue;
    const commit = this.pendingCommit;
    this.pendingValue = undefined;
    this.pendingCommit = undefined;
    if (value !== undefined && commit) commit(value);
  }
}
